import type { EventFeatureFlags } from './event-features.js';
import {
  BONUS_TIER_MAX_COUNT,
  validateBonusTiers,
  type CommissionShiftBasis,
} from './helper-bonus.js';
import { berlinDaysBetween, berlinInstant, berlinParts } from './berlin-time.js';
import type { LabelColorRules } from './label-colors.js';
import type { RequestReminderHours, ShiftReminderMode } from './message-settings.js';
import type { CancelPolicy, LatePolicy, SwapPolicy } from './shiftRequests.js';
import type { SlotBookingMode, SlotKind, StoredRemindAudience } from './time-slots.js';

/**
 * Blaupause eines Basars (Plan 16, U16a): die Beschreibung eines Basars OHNE Personen und OHNE Datum, mit allen Zeiten
 * relativ zum Basartag. „Basar wiederholen“, Standardsets und eigene Vorlagen sind dasselbe Format. Dieses Modul enthält
 * nur Typen, die Zeitrechnung und die Prüfliste (welche Spalte kopiert wird, welche nie). Prüfen der Eingabe (zod) und
 * Lesen/Anlegen in der Datenbank liegen in der API (`events/eventBlueprint*.ts`).
 *
 * Nie in einer Blaupause: Personen, Anmeldungen, Artikel und Etikettencodes, Verkäufe, Kassen und Geräte, Kopplungs-Token,
 * PIN, Nachrichten, Lizenz. Die Liste `BLUEPRINT_COLUMNS` unten hält das je Spalte fest.
 */

export const BLUEPRINT_VERSION = 1;

/**
 * Berliner Kalendertag relativ zum Basartag (Tag des Verkaufsbeginns) und Berliner Uhrzeit („HH:MM“, bei Sekunden
 * „HH:MM:SS“). Dasselbe Prinzip wie `shiftBerlinDays`: hält über die Zeitumstellung, die Uhrzeit bleibt.
 */
export interface RelTime {
  /** Ganze Kalendertage, negativ = vor dem Basartag. */
  day: number;
  at: string;
}

/**
 * Äußerste Grenze der Tage in einer relativen Zeit (gut ein Jahr davor und danach), nur für das Schema der API. Die Kopie
 * eines Basars (`fromEvent` → `apply`) prüft sie bewusst nicht, damit ein altes Vorbild das Wiederholen nie blockiert.
 * Standardsets und eigene Vorlagen (U16b) bekommen engere Grenzen (Plan 16 §4.8.2: Zeiten −14 bis +14 Tage, Anmeldefenster
 * −120 bis 0); „Als Vorlage speichern“ muss die Blaupause darum vor dem Speichern mit diesem engeren Schema prüfen.
 */
export const REL_DAY_LIMIT = 366;
export const REL_AT_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

const pad = (value: number): string => String(value).padStart(2, '0');

/** Rechnet einen Zeitpunkt in eine relative Zeit um. Millisekunden entfallen, Sekunden bleiben nur, wenn sie nicht 0 sind. */
export function relTimeOf(instant: Date, anchor: Date): RelTime {
  const p = berlinParts(instant);
  const at = `${pad(p.hour)}:${pad(p.minute)}${p.second === 0 ? '' : `:${pad(p.second)}`}`;
  return { day: berlinDaysBetween(anchor, instant), at };
}

/** Der Zeitpunkt zu einer relativen Zeit, wenn der Basartag der Berliner Kalendertag von `anchor` ist. */
export function resolveRelTime(rel: RelTime, anchor: Date): Date {
  const a = berlinParts(anchor);
  const [hour = '0', minute = '0', second = '0'] = rel.at.split(':');
  return berlinInstant(
    a.year,
    a.month,
    a.day + rel.day,
    Number(hour),
    Number(minute),
    Number(second),
  );
}

export type LabelNameMode = 'number_first_name' | 'number_initial' | 'number_only';
export type SettlementModel = 'mediation' | 'commission';
export type SelfRegistrationMode = 'approval' | 'auto';
export type ShiftSignupMode = 'team' | 'accounts' | 'public';
export type StationQuickAccess = 'off' | 'name';

export interface BlueprintShift {
  name: string;
  startsAt: RelTime | null;
  endsAt: RelTime | null;
  capacity: number | null;
  location: string | null;
  description: string | null;
  signupMode: ShiftSignupMode;
  waitlistEnabled: boolean;
}

export interface BlueprintScheduleItem {
  label: string;
  startsAt: RelTime;
  endsAt: RelTime | null;
  note: string | null;
  remindAudience: StoredRemindAudience | null;
  remindDaysBefore: number | null;
}

export interface BlueprintTimeSlot {
  kind: SlotKind;
  startsAt: RelTime;
  endsAt: RelTime;
  capacity: number;
  location: string | null;
  note: string | null;
}

/**
 * Kassenplatz als Struktur: Bezeichnung, Zugang und ob eine PIN verlangt ist. Nie die PIN selbst, Token oder Geräte: Jeder
 * neue Platz bekommt ein eigenes Token und, wenn `pin` gesetzt ist, eine NEUE, zufällige PIN. So wird aus einem Platz mit
 * PIN-Schutz beim Kopieren nie still ein Platz ohne.
 */
export interface BlueprintStation {
  label: string;
  quickAccess: StationQuickAccess;
  /** Nur mit Schnellzugang `name` wirksam. */
  pin: boolean;
}

export interface BlueprintShiftRules {
  cancelPolicy: CancelPolicy;
  cancelLateHours: number | null;
  cancelLatePolicy: LatePolicy;
  swapPolicy: SwapPolicy;
  offerEnabled: boolean;
  offerNeedsApproval: boolean;
}

export interface BlueprintBonusTier {
  label: string;
  minShifts: number;
  manualOnly: boolean;
  extraItems: number | null;
  commissionBasisPoints: number | null;
  earlyEntryMinutes: number | null;
  /** Andere Verkäufergebühr der Stufe in ganzen Cent, `null` = Gebühr des Basars. Fehlt bei älteren Vorlagen. */
  feeCents?: number | null;
  /** „Infos verstecken“: die Stufe wirkt, wird aber nie genannt. Fehlt bei älteren Vorlagen = nicht versteckt. */
  infoHidden?: boolean;
}

/**
 * Bonusstufen beim Anwenden einer Vorlage: höchstens sieben, Provision auf die des neuen Basars begrenzt, Minimum
 * Schichten 0 bei „nur zuweisen“. Fehlerhafte Stufen (zum Beispiel Gebühr über der Obergrenze) fallen weg, damit eine
 * von außen gelieferte Vorlage nie scheitert. Zuweisungen je Person gibt es in der Vorlage nicht. Die Stufengebühr darf
 * über der Basargebühr liegen (Entscheidung des Auftraggebers, Welle 2a) – das gilt seit Review W2 nicht mehr: Eine
 * Stufengebühr über der Gebühr des Basars fällt beim Anwenden weg.
 */
export function bonusTiersToApply(
  tiers: readonly BlueprintBonusTier[] | undefined,
  baseCommissionBasisPoints: number,
  baseFeeCents: number | null = null,
): BlueprintBonusTier[] {
  const clamped: BlueprintBonusTier[] = (tiers ?? [])
    .slice(0, BONUS_TIER_MAX_COUNT)
    .map((tier) => ({
      ...tier,
      minShifts: tier.manualOnly ? 0 : tier.minShifts,
      commissionBasisPoints:
        tier.commissionBasisPoints === null
          ? null
          : Math.min(tier.commissionBasisPoints, baseCommissionBasisPoints),
      feeCents: tier.feeCents ?? null,
      infoHidden: tier.infoHidden === true,
    }));
  const bad = new Set(
    validateBonusTiers(clamped, baseCommissionBasisPoints, baseFeeCents).map(
      (problem) => problem.index,
    ),
  );
  return clamped.filter((_, index) => !bad.has(index));
}

export interface BlueprintMessages {
  shiftReminder: ShiftReminderMode;
  requestReminderHours: RequestReminderHours;
  pushSales: boolean;
  pushPickup: boolean;
}

export interface EventBlueprint {
  version: typeof BLUEPRINT_VERSION;
  /** Stammdaten */
  description: string | null;
  venue: string | null;
  /** Beginn des Verkaufs, Ende des Verkaufs, Ende des Basars. Ohne Beginn im Vorbild gibt es keine Zeiten. */
  startsAt: RelTime | null;
  endsAt: RelTime | null;
  closesAt: RelTime | null;
  /** Anmeldung */
  registration: {
    opensAt: RelTime | null;
    closesAt: RelTime | null;
    /**
     * Vorrangfenster: so viele Minuten vor `opensAt` dürfen Eingeladene sich anmelden, `null` = kein Vorrang. Echte Minuten
     * (kein Wanduhr-Abstand): Liegt eine Zeitumstellung zwischen Vorrang und Anmeldestart, verschiebt sich die Uhrzeit des
     * Vorrangs um eine Stunde. Bei den üblichen Vorläufen von Stunden ist das ohne Belang.
     */
    priorityLeadMinutes: number | null;
    mode: SelfRegistrationMode;
    maxSellers: number | null;
    maxItemsPerSeller: number;
    slotBooking: SlotBookingMode;
    /** Automatische Anmeldung bei voller Höchstzahl: Warteliste statt Ablehnung. Fehlt = Ablehnung (Standard, älteres Vorbild). */
    waitlistWhenFull?: boolean;
    /** Helferschicht umgeht die Höchstzahl der Verkäufer. Fehlt = an (Standard). */
    shiftBypassesSellerLimit?: boolean;
    /** Inaktivitätslauf an (Standard aus). Fehlt = aus. */
    inactivityEnabled?: boolean;
    /** Tage ohne erfassten Artikel bis zur Erinnerung (Standard 14). Fehlt = 14. */
    inactivityRemindDays?: number;
    /** Karenztage nach der Erinnerung bis zur Deaktivierung (Standard 7). Fehlt = 7. */
    inactivityGraceDays?: number;
    /** Erste Verkäufernummer (1 bis 9999). Fehlt = 1 (Standard, älteres Vorbild). */
    sellerNumberStart?: number;
    /** Telefon bei der Anmeldung: fehlt = nicht abfragen (Standard). Nur Abweichungen stehen in der Vorlage. */
    sellerPhoneMode?: 'optional' | 'required';
    /** Anschrift bei der Anmeldung: fehlt = freiwillig (Standard). Nur Abweichungen stehen in der Vorlage. */
    sellerAddressMode?: 'off' | 'required';
    /** Verkäufer bei Übernahme einer Helferschicht automatisch freischalten: fehlt = aus (Standard). Nur bei `true` gesetzt. */
    shiftAutoApprove?: true;
    /** Annahme vermerkt beim Check-in die Zustimmung zu den Bedingungen: fehlt = aus (Standard). Nur bei `true` gesetzt. */
    intakeConfirmTerms?: true;
  };
  /** Artikel und Etiketten */
  items: {
    categories: string[] | null;
    sizes: string[] | null;
    photoRecognition: boolean;
    labelNameMode: LabelNameMode;
    colorRules: LabelColorRules | null;
    returnsBasket: boolean;
    /** Spende erlaubt; fehlt bei alten Vorlagen und im Standard (an). Nur bei „aus“ gesetzt (Vorlage bleibt für das vorige Image lesbar). */
    donationsEnabled?: boolean;
    /** Vorhandene Etiketten erlaubt; fehlt = an. Nur bei „aus“ gesetzt. */
    externalCodesEnabled?: boolean;
    /**
     * Preisregeln (Paket B): Mindestpreis in Cent (Standard 50) und Preisschritt in Cent (Standard 1). Nur gesetzt, wenn vom
     * Standard abweichend (Vorlage bleibt für das vorige Image lesbar).
     */
    minPriceCents?: number;
    priceStepCents?: number;
    /** Standard-Etikettenformat (Schlüssel aus `LABEL_GRIDS`), nur bei Abweichung vom Standard. */
    labelGridDefault?: string;
    /** Verkäufer sehen den Verkaufsstand vor der Abrechnung (Audit 21); fehlt = ja, nur bei „nein“ gesetzt. */
    sellersSeeSales?: boolean;
  };
  /** Kasse */
  till: {
    discountPromosEnabled: boolean;
    /** Kasse darf Preise korrigieren; fehlt bei alten Vorlagen und im Standard (an). Nur bei „aus“ gesetzt (Paket F). */
    priceChangeEnabled?: boolean;
    /** Verkauf ohne Etikett (Freier Betrag, Artikel ohne Etikett) erlaubt; fehlt = an. Nur bei „aus“ gesetzt. */
    noLabelEnabled?: boolean;
    stations: BlueprintStation[];
  };
  /** Abrechnung */
  settlement: {
    model: SettlementModel;
    commissionBasisPoints: number;
    sellerFeeCents: number;
    payoutRoundingCents: number;
    /**
     * Mindestprovision in Cent, Helfer ohne Provision, Helfer ohne Gebühr (Paket C). Nur gesetzt, wenn vom Standard (0 Cent, ja,
     * nein) abweichend: So bleibt eine Vorlage für das vorige Image lesbar, „fehlt“ heißt Standard.
     */
    minCommissionCents?: number;
    helperCommissionExempt?: boolean;
    helperFeeExempt?: boolean;
    /** Verkäufergebühr nur bei Umsatz (Standard nein), nur bei Abweichung gesetzt. */
    feeOnlyIfSold?: boolean;
    /** Vorlage des Verwendungszwecks der Auszahlung (Paket G), nur bei Abweichung vom Standard. */
    payoutRemittanceTemplate?: string;
  };
  /** Helfer: Schichten (Eintragung immer geschlossen) und die Regeln für Absage und Tausch (Plan 13, `null` = Standard) */
  helpers: { shifts: BlueprintShift[]; rules: BlueprintShiftRules | null };
  /**
   * Zusatzfunktionen (Plan Welle 3, F0). Fehlt bei alten Vorlagen: dann ist alles aus. Ein Schalter, der (noch) nicht
   * freigegeben ist, wird beim Anwenden auf aus gesetzt (`effectiveFeatureFlags`), die Vorlage scheitert nicht.
   */
  features?: EventFeatureFlags;
  /**
   * Bonusstufen des Helferbonus (Plan Welle 3, W3-Bonus-a). Fehlt bei alten Vorlagen und bei Basaren ohne Stufen. Die
   * Zuweisungen je Verkäufer (`bonus_tier_id`) kommen nie mit (Personenbezug).
   */
  bonusTiers?: BlueprintBonusTier[];
  /** Was bei der Provision als Schicht zählt; fehlt = zugesagte (Standard). Nur bei `confirmed` gesetzt. */
  bonusCommissionBasis?: CommissionShiftBasis;
  /**
   * Nachrichten und Erinnerungen (Paket D). Fehlt bei alten Vorlagen und bei Basaren mit den Standardwerten: dann gelten
   * die Standardwerte (Erinnerung am Vortag, Team-Erinnerung 24 Stunden, beide Mitteilungsarten an).
   */
  messages?: BlueprintMessages;
  /** Früher Einlass ohne Freischaltung (W3-Bonus-b); fehlt = nur nach Freischaltung (Standard). Nur bei `false` gesetzt. */
  bonusEntryRequiresUnlock?: false;
  /** Bonusstufen auf der öffentlichen Anmeldeseite zeigen; fehlt = aus (Standard). Nur bei `true` gesetzt. */
  bonusPublic?: true;
  /** Online-Basar (Welle 2b); fehlt = nein (Standard). Nur bei `true` gesetzt. */
  online?: true;
  /** Ablaufplan mit Erinnerungen */
  schedule: BlueprintScheduleItem[];
  timeSlots: BlueprintTimeSlot[];
  /** Bedingungen (neueste Fassung, Text) */
  terms: string | null;
}

// --- Prüfliste ---------------------------------------------------------------------------------------------------------

/** Entscheidung je Spalte: kopiert (Pfad in der Blaupause) oder nie kopiert (mit Begründung). */
export type ColumnDecision = { copy: string } | { exclude: string };

const exclude = (reason: string): ColumnDecision => ({ exclude: reason });
const copy = (path: string): ColumnDecision => ({ copy: path });

const REASON_NEW = 'Wird für den neuen Basar neu erzeugt.';
const REASON_PERSON = 'Personenbezug, wird nie kopiert.';
const REASON_LICENSE =
  'Lizenz: nie kopiert, der neue Basar bekommt die Gratis-Stufe (Buchung und Widerrufsverzicht laufen wie beim Anlegen).';
const REASON_TEAM =
  'Team: Personenbezug, nie Teil der Blaupause. Beim Kopieren nur über die Option „Team“ (aktive Mitglieder der Organisation).';
const REASON_STATE = 'Laufender Zustand des alten Basars, der neue beginnt leer.';

/**
 * Jede Spalte der Tabellen, die eine Kopie berührt, ist hier entweder kopiert oder mit Grund ausgeschlossen. Der Test
 * (`apps/api/src/events/eventBlueprintService.test.ts`) vergleicht das mit den Spalten der echten Datenbank: Eine neue Spalte ohne Entscheidung
 * lässt ihn fehlschlagen. So geht kein Rahmendatum mehr unbemerkt verloren (14b F20).
 */
export const BLUEPRINT_COLUMNS: Readonly<Record<string, Readonly<Record<string, ColumnDecision>>>> =
  {
    events: {
      id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant), nie aus der Quelle.'),
      slug: exclude('Aus dem neuen Titel erzeugt.'),
      title: exclude('Gibt der Veranstalter beim Kopieren neu ein.'),
      description: copy('description'),
      status: exclude('Der neue Basar beginnt immer als Entwurf.'),
      venue: copy('venue'),
      starts_at: copy('startsAt'),
      ends_at: copy('endsAt'),
      closes_at: copy('closesAt'),
      registration_opens_at: copy('registration.opensAt'),
      registration_closes_at: copy('registration.closesAt'),
      commission_basis_points: copy('settlement.commissionBasisPoints'),
      seller_fee_cents: copy('settlement.sellerFeeCents'),
      max_sellers: copy('registration.maxSellers'),
      max_items_per_seller: copy('registration.maxItemsPerSeller'),
      expected_items: exclude(
        'Bestimmt die Preisstufe der Lizenz: wird beim Anlegen abgefragt (Kopie: auf die Gratis-Stufe gekürzt).',
      ),
      label_name_mode: copy('items.labelNameMode'),
      created_by: exclude('Die anlegende Person des neuen Basars.'),
      created_at: exclude(REASON_NEW),
      updated_at: exclude(REASON_NEW),
      deleted_at: exclude(REASON_STATE),
      label_salt: exclude(
        'Etikettencodes: immer neuer, zufälliger Startwert, nie der alte (Regel 4).',
      ),
      next_seller_number: exclude(
        'Zähler des neuen Basars: beginnt bei dessen Startnummer (seller_number_start), nie beim Stand des alten.',
      ),
      settlement_model: copy('settlement.model'),
      settlement_finalized_at: exclude(REASON_STATE),
      marketplace_listed: exclude(
        'Marktplatz-Freigabe ist immer aus und wird bewusst neu erteilt.',
      ),
      self_registration_mode: copy('registration.mode'),
      item_categories: copy('items.categories'),
      item_sizes: copy('items.sizes'),
      photo_recognition_enabled: copy('items.photoRecognition'),
      returns_basket_mode: copy('items.returnsBasket'),
      label_color_rules: copy('items.colorRules'),
      slot_booking: copy('registration.slotBooking'),
      waitlist_when_full: copy('registration.waitlistWhenFull'),
      shift_bypasses_seller_limit: copy('registration.shiftBypassesSellerLimit'),
      inactivity_enabled: copy('registration.inactivityEnabled'),
      inactivity_remind_days: copy('registration.inactivityRemindDays'),
      inactivity_grace_days: copy('registration.inactivityGraceDays'),
      seller_number_start: copy('registration.sellerNumberStart'),
      seller_phone_mode: copy('registration.sellerPhoneMode'),
      seller_address_mode: copy('registration.sellerAddressMode'),
      shift_auto_approve: copy('registration.shiftAutoApprove'),
      intake_confirm_terms: copy('registration.intakeConfirmTerms'),
      copied_from_event_id: exclude('Nachverfolgung, setzt der Kopiervorgang selbst.'),
      created_from: exclude(
        'Nachverfolgung (system:<key>@<version>, template:<id>, event:<id>), setzt das Anlegen selbst.',
      ),
      priority_registration_opens_at: copy('registration.priorityLeadMinutes'),
      payout_rounding_cents: copy('settlement.payoutRoundingCents'),
      discount_promos_enabled: copy('till.discountPromosEnabled'),
      schedule_suggest: exclude(
        'Zeitplan-Vorschlag ist eine Einstellung dieses Basars, der neue startet ohne Vorschlag.',
      ),
      donations_enabled: copy('items.donationsEnabled'),
      external_codes_enabled: copy('items.externalCodesEnabled'),
      min_item_price_cents: copy('items.minPriceCents'),
      price_step_cents: copy('items.priceStepCents'),
      card_payment_enabled: exclude(
        'Kartengerät des Vereins (U14): wird je Basar neu entschieden, Standard aus.',
      ),
      invoice_enabled: exclude(
        'Kaufbeleg beim Abkassieren (Welle 4f): steuerliche Behandlung (TSE) ist Sache des Veranstalters, wird je Basar neu entschieden, Standard aus.',
      ),
      ai_list_import_enabled: exclude(
        'KI-Artikelliste hochladen (Welle 4h): Text oder Foto geht an einen KI-Dienstleister (Mistral AI), wird je Basar neu entschieden, Standard aus.',
      ),
      ai_image_match_enabled: exclude(
        'KI-Bildzuordnung an der Annahme (Welle 4k): das Foto eines Artikels geht an einen KI-Dienstleister (Mistral AI), wird je Basar neu entschieden, Standard aus.',
      ),
      item_images_enabled: exclude(
        'Artikelbilder (Welle 4g): können Personen zeigen, werden je Basar neu entschieden, Standard aus.',
      ),
      run_sheet_enabled: copy('features.runSheet'),
      push_enabled: copy('features.push'),
      payout_transfer_enabled: copy('features.payoutTransfer'),
      helper_bonus_enabled: copy('features.helperBonus'),
      commission_shift_basis: copy('bonusCommissionBasis'),
      shift_reminder_mode: copy('messages.shiftReminder'),
      request_reminder_hours: copy('messages.requestReminderHours'),
      push_sales_enabled: copy('messages.pushSales'),
      push_pickup_enabled: copy('messages.pushPickup'),
      min_commission_cents: copy('settlement.minCommissionCents'),
      helper_commission_exempt: copy('settlement.helperCommissionExempt'),
      helper_fee_exempt: copy('settlement.helperFeeExempt'),
      fee_only_if_sold: copy('settlement.feeOnlyIfSold'),
      entry_requires_unlock: copy('bonusEntryRequiresUnlock'),
      bonus_public: copy('bonusPublic'),
      is_online: copy('online'),
      till_price_change_enabled: copy('till.priceChangeEnabled'),
      till_no_label_enabled: copy('till.noLabelEnabled'),
      payout_remittance_template: copy('settlement.payoutRemittanceTemplate'),
      label_grid_default: copy('items.labelGridDefault'),
      sellers_see_sales: copy('items.sellersSeeSales'),
    },
    event_schedule_items: {
      id: exclude(REASON_NEW),
      event_id: exclude(REASON_NEW),
      position: copy('schedule (Reihenfolge)'),
      label: copy('schedule[].label'),
      starts_at: copy('schedule[].startsAt'),
      ends_at: copy('schedule[].endsAt'),
      note: copy('schedule[].note'),
      remind_audience: copy('schedule[].remindAudience'),
      remind_days_before: copy('schedule[].remindDaysBefore'),
    },
    helper_shifts: {
      id: exclude(REASON_NEW),
      event_id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant).'),
      name: copy('helpers.shifts[].name'),
      starts_at: copy('helpers.shifts[].startsAt'),
      ends_at: copy('helpers.shifts[].endsAt'),
      created_at: exclude(REASON_NEW),
      capacity: copy('helpers.shifts[].capacity'),
      location: copy('helpers.shifts[].location'),
      description: copy('helpers.shifts[].description'),
      signup_mode: copy('helpers.shifts[].signupMode'),
      waitlist_enabled: copy('helpers.shifts[].waitlistEnabled'),
      gap_notified_at: exclude(REASON_STATE),
    },
    event_shift_settings: {
      event_id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant).'),
      signup_release: exclude(
        'Die Eintragung ist im neuen Basar immer geschlossen, die Freigabe geschieht bewusst.',
      ),
      signup_opens_at: exclude(
        'Gehört zur Freigabe der Eintragung (immer geschlossen, ohne Zeitpunkt).',
      ),
      updated_by: exclude(REASON_PERSON),
      updated_at: exclude(REASON_NEW),
      cancel_policy: copy('helpers.rules.cancelPolicy'),
      cancel_late_hours: copy('helpers.rules.cancelLateHours'),
      cancel_late_policy: copy('helpers.rules.cancelLatePolicy'),
      swap_policy: copy('helpers.rules.swapPolicy'),
      offer_enabled: copy('helpers.rules.offerEnabled'),
      offer_needs_approval: copy('helpers.rules.offerNeedsApproval'),
      requests_updated_by: exclude(REASON_PERSON),
      requests_updated_at: exclude(REASON_STATE),
      requests_notice_ack_at: exclude('Der einmalige Hinweis (Plan 13) gilt für jeden Basar neu.'),
    },
    event_bonus_tiers: {
      id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant).'),
      event_id: exclude(REASON_NEW),
      position: copy('bonusTiers (Reihenfolge)'),
      label: copy('bonusTiers[].label'),
      min_shifts: copy('bonusTiers[].minShifts'),
      manual_only: copy('bonusTiers[].manualOnly'),
      extra_items: copy('bonusTiers[].extraItems'),
      commission_basis_points: copy('bonusTiers[].commissionBasisPoints'),
      early_entry_minutes: copy('bonusTiers[].earlyEntryMinutes'),
      fee_cents: copy('bonusTiers[].feeCents'),
      info_hidden: copy('bonusTiers[].infoHidden'),
      created_at: exclude(REASON_NEW),
    },
    event_time_slots: {
      id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant).'),
      event_id: exclude(REASON_NEW),
      kind: copy('timeSlots[].kind'),
      starts_at: copy('timeSlots[].startsAt'),
      ends_at: copy('timeSlots[].endsAt'),
      capacity: copy('timeSlots[].capacity'),
      location: copy('timeSlots[].location'),
      note: copy('timeSlots[].note'),
      created_at: exclude(REASON_NEW),
    },
    register_stations: {
      id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant).'),
      event_id: exclude(REASON_NEW),
      label: copy('till.stations[].label'),
      quick_access: copy('till.stations[].quickAccess'),
      pin_hash: exclude(
        'Zugangsdaten werden nie kopiert, auch nicht als Hash. Nur ob eine PIN verlangt ist, kommt als `pin` mit: Der neue Platz bekommt eine neue, zufällige PIN.',
      ),
      pin_sealed: exclude('Zugangsdaten werden nie kopiert.'),
      max_devices: exclude(
        'Gerätegrenze bleibt beim Standard des neuen Platzes (Geräte kommen nie mit).',
      ),
      token_hash: exclude('Kopplungs-Token: jeder neue Platz bekommt ein eigenes, nie das alte.'),
      token_sealed: exclude('Kopplungs-Token: jeder neue Platz bekommt ein eigenes, nie das alte.'),
      token_created_at: exclude(REASON_NEW),
      pairing_locked_until: exclude(REASON_STATE),
      created_by: exclude(REASON_PERSON),
      created_at: exclude(REASON_NEW),
      deleted_at: exclude('Gelöschte Plätze kommen nicht mit.'),
      pin_lock_notified_at: exclude(REASON_STATE),
    },
    event_terms: {
      id: exclude(REASON_NEW),
      event_id: exclude(REASON_NEW),
      organization_id: exclude('Kommt aus dem Pfad der Anfrage (Mandant).'),
      content: copy('terms'),
      created_by: exclude(REASON_PERSON),
      created_at: exclude(
        'Die Fassung ist im neuen Basar eine neue Zeile mit neuem Zeitstempel (Beweiszweck).',
      ),
    },
    event_licenses: {
      event_id: exclude(REASON_LICENSE),
      tier: exclude(REASON_LICENSE),
      item_limit: exclude(REASON_LICENSE),
      items_created: exclude(REASON_LICENSE),
      price_gross_cents: exclude(REASON_LICENSE),
      vat_percent: exclude(REASON_LICENSE),
      status: exclude(REASON_LICENSE),
      activated_at: exclude(REASON_LICENSE),
      activated_by: exclude(REASON_LICENSE),
      created_at: exclude(REASON_LICENSE),
      pending_tier: exclude(REASON_LICENSE),
      pending_since: exclude(REASON_LICENSE),
      waiver_text_id: exclude(REASON_LICENSE),
      waiver_accepted_at: exclude(REASON_LICENSE),
    },
    event_members: {
      event_id: exclude(REASON_TEAM),
      user_id: exclude(REASON_TEAM),
      role: exclude(REASON_TEAM),
      created_at: exclude(REASON_TEAM),
    },
  };

/** Tabellen, deren Zeilen eine Kopie nie anlegt, die aber Spalten der Prüfliste haben (nur zur Vollständigkeit). */
export const BLUEPRINT_TABLES = Object.keys(BLUEPRINT_COLUMNS);

const NEVER_PERSON = 'Personen, Konten und ihre Daten: nie Teil einer Blaupause oder Kopie.';
const NEVER_CODES =
  'Artikel und Etikettencodes (Regel 4): nie kopiert, Codes werden nie wiederverwendet.';
const NEVER_MONEY = 'Verkäufe, Bargeld und Abrechnung: laufender Zustand des alten Basars.';
const NEVER_ACCESS =
  'Zugänge, Geräte, Sitzungen und Token: nie kopiert, jeder neue Basar bekommt eigene.';
const NEVER_MESSAGES =
  'Nachrichten, Rundschreiben, Neuigkeiten und Plakate: gehören zum alten Basar.';
const NEVER_LOG = 'Protokolle und Zähler: gehören zum alten Basar.';

/**
 * Alle übrigen Tabellen mit einer Spalte `event_id`: Eine Kopie legt nie Zeilen darin an. Der Test sorgt dafür, dass eine
 * NEUE Tabelle mit `event_id` nicht ohne Entscheidung entsteht (entweder hier oder in `BLUEPRINT_COLUMNS`).
 */
export const BLUEPRINT_NEVER_COPIED_TABLES: Readonly<Record<string, string>> = {
  audit_log: NEVER_LOG,
  notification_throttle: NEVER_LOG,
  schedule_reminder_sends: NEVER_LOG,
  push_marks:
    'Merkzeiten der Mitteilungen (Plan 17): laufender Zustand des alten Basars, der Schalter `push_enabled` kommt mit.',
  push_outbox:
    'Mitteilungen im Ausgang (Plan 17): gehören zum alten Basar und zu Geräten von Personen, nie kopiert.',
  event_entry_passes:
    'Einlass-Pässe (W3-Bonus-b): Personenbezug und Token, werden je Basar neu erzeugt, nie kopiert.',
  event_entry_unlocks:
    'Freischaltungen für früheren Einlass (W3-Bonus-b): Personenbezug, gehören zum alten Basar, nie kopiert.',
  discount_promos:
    'Rabattaktionen: laufen im Basar, die Erlaubnis (`discount_promos_enabled`) kommt mit, die Aktionen nie.',
  promo_code_redemptions: 'Eingelöste Gutscheine gehören zur Lizenz des alten Basars.',
  item_images:
    'Artikelbilder (Welle 4g, 0113): Personenbezug möglich (Fotos können Personen oder Orte zeigen), gehören zu Artikeln des alten Basars, nie kopiert.',
  registration_signatures:
    'Digitale Unterschriften (Welle 3, 0110): Personenbezug und Bild der Unterschrift, gehören zu einer Anmeldung des alten Basars, nie kopiert.',
  event_signature_settings:
    'Schalter der digitalen Unterschrift (Welle 3, 0109): wird bewusst je Basar neu eingeschaltet (Personenbezug), nie kopiert.',
  event_posters: NEVER_MESSAGES,
  messages: NEVER_MESSAGES,
  message_recipients: NEVER_MESSAGES,
  message_threads: NEVER_MESSAGES,
  news_posts: NEVER_MESSAGES,
  event_seller_invites:
    'Einladungen früherer Verkäufer: Personenbezug, werden je Basar neu ausgelöst.',
  invitation_event_roles: 'Einladungen ins Team: Personenbezug, nie kopiert.',
  helper_invites: NEVER_PERSON,
  helper_requests: NEVER_PERSON,
  helper_shift_links:
    'Eintragungs-Links der Schichten: nie kopiert, die Veranstalterin erzeugt neue.',
  helper_shift_requests: NEVER_PERSON,
  helper_shift_signups: 'Eintragungen von Helfern: Personenbezug, nie kopiert.',
  seller_registrations: NEVER_PERSON, // inkl. crate_count, intake_terms_confirmed_at/_by, checked_in_at/_by (Welle 4e)
  seller_slot_bookings:
    'Buchungen von Zeitfenstern: Personenbezug, nie kopiert (die Fenster selbst kommen mit).',
  event_seller_groups:
    'Aktivierung einer Verkäufer-Gruppe (Welle 2b, 0106): gehört zu Personen der Gruppe und zum alten Basar, wird je Basar neu entschieden, nie kopiert.',
  event_reserved_numbers:
    'VIP-Reservierungen von Verkäufernummern (Welle 1, 0091): Nummern gehören zu einem Basar und zu Personen der Anmeldung, werden je Basar neu gesetzt, nie kopiert.',
  items: NEVER_CODES,
  settlements: NEVER_MONEY,
  settlement_adjustments: NEVER_MONEY,
  settlement_items: NEVER_MONEY,
  settlement_late_bookings: NEVER_MONEY,
  sales: NEVER_MONEY,
  sale_lines: NEVER_MONEY,
  sale_quarantine: NEVER_MONEY,
  registers: NEVER_MONEY,
  register_cash_counts: NEVER_MONEY,
  // Kommentare der Kassen und Fotos zu Artikeln ohne Etikett (U17f, U17h): gehören zu den Verkäufen, nie in eine Vorlage
  register_notes: NEVER_MONEY,
  sale_photos: NEVER_MONEY,
  sale_invoices:
    'Kaufbelege zu Verkäufen (Welle 4f): fortlaufende Nummern je Basar, gehören zu den Verkäufen des alten Basars, nie kopiert.',
  event_invite_schedules:
    'Geplanter Versand der Vorab-Mail an frühere Verkäufer: gehört zum alten Basar, ein neuer Basar plant neu.',
  register_device_sessions: NEVER_ACCESS,
  register_station_devices: NEVER_ACCESS,
  register_open_codes: NEVER_ACCESS,
  event_sessions: NEVER_ACCESS,
  support_grants: NEVER_ACCESS,
};
